import React, { ReactNode } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { palette, spaces, fontFeatureSettings } from '../../assets/styles/themes/tokens';
import useScreenSize from '../../utils/useScreenSize';

const mobileBarOuterSx: SxProps<Theme> = {
  display: 'flex',
  padding: `${spaces.sm} ${spaces.md}`,
  alignItems: 'center',
  gap: spaces.xs,
  alignSelf: 'stretch',
  borderBottom: `1px solid ${palette.elevation.outlined}`,
  backgroundColor: palette.background.paperElevation2,
};

const rowSxBase: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces.xs,
};

const defaultTitleSx: SxProps<Theme> = {
  color: palette.text.primary,
  fontFeatureSettings: fontFeatureSettings.disableLigatures,
};

export interface AssistantSectionTopBarProps {
  icon: ReactNode;
  title: ReactNode;
  trailing?: ReactNode;
  /**
   * When true, never wraps in the mobile bar shell (use when the page already provides
   * e.g. breadcrumb bar styles on small screens).
   */
  alwaysRow?: boolean;
  /** Merged onto the inner row (e.g. `{ height: 24 }` for master layout top bar) */
  rowSx?: SxProps<Theme>;
  /** Merged onto the title Typography */
  titleSx?: SxProps<Theme>;
  'data-testid'?: string;
}

/**
 * Shared header row for Mukibude-related pages. On small screens uses a full-width mobile
 * bar unless `alwaysRow` is set (e.g. when the page already wraps the row in a breadcrumb bar).
 */
const AssistantSectionTopBar: React.FC<AssistantSectionTopBarProps> = ({
  icon,
  title,
  trailing,
  alwaysRow = false,
  rowSx,
  titleSx,
  'data-testid': dataTestId,
}) => {
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isSmallScreen = isXsScreen || isSmScreen;
  const useMobileBarShell = !alwaysRow && isSmallScreen;

  const boxSx = (rowSx ? [rowSxBase, rowSx] : rowSxBase) as SxProps<Theme>;
  const titleMergedSx = (titleSx
    ? [defaultTitleSx, titleSx]
    : defaultTitleSx) as SxProps<Theme>;

  const rowContent = (
    <>
      {icon}
      <Typography variant='body2' sx={titleMergedSx}>
        {title}
      </Typography>
      {trailing}
    </>
  );

  if (useMobileBarShell) {
    return (
      <Box data-testid={dataTestId} sx={mobileBarOuterSx}>
        <Box sx={boxSx}>{rowContent}</Box>
      </Box>
    );
  }

  return (
    <Box data-testid={dataTestId} sx={boxSx}>
      {rowContent}
    </Box>
  );
};

export default AssistantSectionTopBar;
